/**
 * @param {Element | null} root
 * @returns {number[]}
 */
function cascadeGetBundleFirstOrderGiftVariantIds(root) {
  if (!root) return [];
  const blocks = root.querySelectorAll('[data-bundle-first-order-gifts]');
  for (let i = 0; i < blocks.length; i += 1) {
    const raw = blocks[i].getAttribute('data-bundle-first-order-gift-variant-ids') || '';
    if (raw.trim()) {
      return raw
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => n > 0);
    }
  }
  return [];
}

function cascadeBundleCartAddUrlJs() {
  let u = theme.routes.cart_add_url;
  if (u && !String(u).includes('.js')) {
    u = String(u).replace(/\/$/, '') + '.js';
  }
  return u;
}

function cascadeParseCartAddResponse(res, text) {
  let data = /** @type {any} */ ({});
  try {
    data = text ? JSON.parse(text) : {};
  } catch (e) {
    data = {
      status: res.status,
      message: res.statusText || 'Cart add response was not JSON',
    };
  }
  if (!res.ok) {
    if (!data.status) data.status = res.status;
    if (!data.message && !data.description) {
      data.message = data.description || text || res.statusText || 'Cart add failed';
    }
  }
  return data;
}

const CASCADE_CART_SECTIONS_DRAWER = 'cart-items,cart-footer,cart-item-count,cart-live-region';

/**
 * Hard safety: never fire the gift cascade more than once per `WINDOW_MS`
 * regardless of how many cart/add.js POSTs pass through our hooks.
 * Protects against misbehaving third parties that re-issue their own adds.
 */
const CASCADE_GIFT_THROTTLE_MS = 4000;
function cascadeGiftAddRecentlyFired() {
  const t = /** @type {number} */ (window.__cascadeBundleGiftLastFiredAt || 0);
  return t > 0 && Date.now() - t < CASCADE_GIFT_THROTTLE_MS;
}
function cascadeMarkGiftAddFired() {
  window.__cascadeBundleGiftLastFiredAt = Date.now();
}

/**
 * Generate a unique bundle id used to tag the main product line and all of
 * its gift line items as a single deletable group. Format keeps it small but
 * very unlikely to collide across simultaneous adds in different tabs.
 *
 * @returns {string}
 */
function cascadeGenerateBundleId() {
  const rand = Math.random().toString(36).slice(2, 8);
  return 'bundle_' + Date.now().toString(36) + '_' + rand;
}

/**
 * Add bundle free-gift line items to the cart one by one, using the exact
 * same request shape as the native theme add-to-cart (FormData, with
 * `sections` + `sections_url`). Every gift response therefore carries fresh
 * `cart-items` / `cart-footer` / `cart-item-count` / `cart-live-region`
 * section HTML, and we progressively dispatch `cascade:modalcart:afteradditem`
 * after each one so `cart-items.js`, `cart-footer.js`, `cart-count.js` all
 * `Alpine.morph` the theme drawer into the new state as the gifts land —
 * identical to what happens when the user adds four separate products in a
 * row.
 *
 * When `bundleId` is provided, every gift line is tagged with hidden
 * `_bundle_id` + `_bundle_role=gift` line item properties so the cascade
 * removal hook can identify them as siblings of the main product line.
 *
 * @param {number[]} giftIdList
 * @param {{ modalCart: boolean, bundleId?: string }} opts
 * @returns {Promise<any|undefined>}
 */
function cascadeAddBundleGiftLineItems(giftIdList, opts) {
  const { modalCart, bundleId } = opts;
  const addUrl = String(theme.routes.cart_add_url || '/cart/add');
  let p = /** @type {Promise<any>} */ (Promise.resolve(null));
  let lastData = null;
  giftIdList.forEach((gid) => {
    p = p.then(() => {
      const fd = new FormData();
      fd.append('id', String(gid));
      fd.append('quantity', '1');
      fd.append('form_type', 'product');
      if (bundleId) {
        fd.append('properties[_bundle_id]', bundleId);
        fd.append('properties[_bundle_role]', 'gift');
      }
      if (modalCart) {
        fd.append('sections', CASCADE_CART_SECTIONS_DRAWER);
        fd.append('sections_url', window.location.pathname);
      }
      return fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: fd,
      })
        .then((res) => res.text().then((text) => cascadeParseCartAddResponse(res, text)))
        .then((data) => {
          if (data && data.status) {
            return Promise.reject(data);
          }
          lastData = data;
          /* Progressive refresh: tell the theme drawer to re-morph after each
             gift lands, exactly like the native single-line add path does. */
          if (modalCart && cascadeCartResponseHasDrawerSections(data)) {
            cascadeDispatchModalCartAfterAdd(data);
          }
          /* Small gap so Slide Cart / other third-party hooks can finish their
             own DOM updates before the next POST. */
          return new Promise((resolve) => setTimeout(resolve, 120));
        });
    });
  });
  return p
    .then(() => lastData)
    .then((d) => {
      cascadeLockBundleGiftCartLinesDebounced();
      return d;
    });
}

/**
 * Theme expects two targets:
 * - `modal-cart.js` listens on **document.body** to open the drawer
 * - `cart-items.js`, `cart-footer.js`, `cart-count.js` register on **window** and Alpine.morph
 *   from `e.detail.response.sections[...]`
 * A CustomEvent on body does not bubble to `window` in the DOM, so we must fire twice.
 * @param {any} response
 */
function cascadeDispatchModalCartAfterAdd(response) {
  const detail = { response };
  const type = 'cascade:modalcart:afteradditem';
  if (document.body) {
    document.body.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
  }
  window.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
}

/**
 * @param {any} data
 * @returns {boolean}
 */
function cascadeCartResponseHasDrawerSections(data) {
  return (
    data &&
    data.sections &&
    typeof data.sections['cart-items'] === 'string' &&
    data.sections['cart-items'].length > 0 &&
    typeof data.sections['cart-item-count'] === 'string'
  );
}

/**
 * Re-fetch section HTML the same way as quantity change (no cart mutation).
 * Use when the last /cart/add.js response is missing `sections` or Slide Cart overwrites the drawer.
 * @param {any} [previous]
 * @returns {Promise<any>}
 */
function cascadeEnsureDrawerResponseWithSections(previous) {
  if (cascadeCartResponseHasDrawerSections(previous)) {
    return Promise.resolve(previous);
  }
  if (typeof theme === 'undefined' || !theme.routes || !theme.routes.cart_change_url) {
    return Promise.resolve(previous);
  }
  if (typeof fetchConfigDefaults !== 'function') {
    return Promise.resolve(previous);
  }
  return fetch('/cart.js', { credentials: 'same-origin' })
    .then((r) => r.json())
    .then((cart) => {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
        return previous;
      }
      const first = cart.items[0];
      const config = fetchConfigDefaults('javascript');
      config.body = JSON.stringify({
        id: first.key,
        quantity: first.quantity,
        sections: CASCADE_CART_SECTIONS_DRAWER,
        sections_url: window.location.pathname,
      });
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      return fetch(theme.routes.cart_change_url, config).then((res) => res.json());
    })
    .then((out) => {
      if (out && out.sections) {
        return out;
      }
      return previous;
    });
}

/**
 * @param {any} [lastData]
 * @param {boolean} [modalCart]
 * @returns {Promise<void>}
 */
function cascadeUpdateDrawerAfterBundleGiftsComplete(lastData, modalCart) {
  /* Always run regardless of modalCart: Slide Cart acts as the drawer even
     when cart_type is "page", so we must always broadcast to third-party
     apps. The `modalCart` flag only controls whether we also morph the
     theme's native Alpine drawer. */
  return cascadeEnsureDrawerResponseWithSections(lastData).then((detail) => {
    if (modalCart && cascadeCartResponseHasDrawerSections(detail)) {
      cascadeAfterDrawerMorphNotifyThemeAndApps(detail);
    } else {
      cascadeBroadcastCartToApps();
    }
    cascadeLockBundleGiftCartLinesDebounced();
  });
}

/**
 * Slide Cart (AllFetch / slidecarthq) public API for re-syncing the drawer.
 * @see https://slide-cart.helpscoutdocs.com/article/87-slide-cart-callbacks-and-methods
 */
function cascadeRefreshSlideCartIfPresent() {
  if (typeof window.SLIDECART_UPDATE === 'function') {
    try {
      window.SLIDECART_UPDATE(function () {});
    } catch (e) {
      /* no-op */
    }
  }
}

/**
 * @param {any} cart
 */
function cascadeSlideCartSetCartIfPresent(cart) {
  if (cart && typeof window.SLIDECART_SET_CART === 'function') {
    try {
      window.SLIDECART_SET_CART(cart);
    } catch (e) {
      /* no-op */
    }
  }
}

/**
 * Check whether Slide Cart's DOM shows as many items as the server cart.
 * Returns `true` when the drawer is rendered but contains strictly fewer
 * line items than the server cart, OR when the drawer is rendered but our
 * heuristics can't count items at all — we err on the side of "stale" to
 * make sure a trailing close/open cycle runs and forces a clean rebuild.
 * @param {number} expected
 */
function cascadeSlideCartIsMissingItems(expected) {
  if (!expected) return false;
  const root =
    document.querySelector('#slidecarthq') ||
    document.querySelector('[class*="slidecart"]');
  if (!root) return false;
  /* Try a wide net — Slide Cart by AllFetch uses varying markup across
     builds. If none match, treat as stale so we force a rerender. */
  const selectors = [
    '.item',
    '[data-item-key]',
    '[data-line-item]',
    '[data-slidecart-item]',
    '.slidecart-item',
    '.cart__item',
    '[data-key]',
    'li[data-variant-id]',
  ];
  let rendered = 0;
  for (let i = 0; i < selectors.length; i += 1) {
    const n = root.querySelectorAll(selectors[i]).length;
    if (n > rendered) rendered = n;
  }
  if (rendered === 0) {
    /* Couldn't count — assume stale when there should be >1 item. */
    return expected > 1;
  }
  return rendered < expected;
}

/**
 * Best-effort open-state detection. Slide Cart exposes `SLIDECART_STATE` on some
 * builds; on others we fall back to DOM sniffing on the known root selectors.
 */
function cascadeSlideCartIsOpen() {
  try {
    if (typeof window.SLIDECART_STATE === 'function') {
      const s = window.SLIDECART_STATE();
      if (s && typeof s === 'object') {
        if (typeof s.isOpen === 'boolean') return s.isOpen;
        if (typeof s.open === 'boolean') return s.open;
      }
    }
  } catch (e) {
    /* fall through to DOM detection */
  }
  const selectors = [
    '#slidecarthq.open',
    '#slidecarthq .slidecarthq.open',
    '.slidecarthq.open',
    '[class*="slidecart"].is-open',
    '[data-slidecart-open="true"]',
  ];
  for (let i = 0; i < selectors.length; i += 1) {
    if (document.querySelector(selectors[i])) {
      return true;
    }
  }
  return false;
}

/**
 * Last resort: Slide Cart's render tree didn't sync to the fresh cart. Toggle
 * CLOSE/OPEN so the app rebuilds the DOM from the current cart object.
 */
let cascadeSlideCartRerenderInFlight = false;
function cascadeForceSlideCartRerenderCycle() {
  if (cascadeSlideCartRerenderInFlight) return;
  const hasOpen = typeof window.SLIDECART_OPEN === 'function';
  const hasClose = typeof window.SLIDECART_CLOSE === 'function';
  if (!hasOpen || !hasClose) return;
  const isOpen = cascadeSlideCartIsOpen();
  cascadeSlideCartRerenderInFlight = true;
  try {
    window.SLIDECART_CLOSE();
  } catch (e) {
    /* no-op */
  }
  setTimeout(() => {
    cascadeRefreshSlideCartIfPresent();
    if (isOpen) {
      try {
        window.SLIDECART_OPEN();
      } catch (e) {
        /* no-op */
      }
    }
    setTimeout(() => {
      cascadeSlideCartRerenderInFlight = false;
    }, 120);
  }, 80);
}

/**
 * Dispatch the events that third-party carts (Skio, Slide Cart, others) commonly
 * listen to on both `document` and `document.body`, with the latest cart payload.
 * @param {any} cart
 */
function cascadeDispatchThirdPartyCartEvents(cart) {
  const targets = [document.body, document];
  const types = ['CartUpdated', 'cart:updated', 'cart:refresh'];
  for (let t = 0; t < targets.length; t += 1) {
    for (let n = 0; n < types.length; n += 1) {
      targets[t].dispatchEvent(
        new CustomEvent(types[n], { detail: cart, bubbles: true })
      );
    }
  }
}

/**
 * True when Slide Cart is installed on the page (either the DOM root exists
 * or its public JS API is present). Used to decide whether the aggressive
 * rerender fallback needs to run at all.
 */
function cascadeHasSlideCart() {
  if (
    typeof window.SLIDECART_UPDATE === 'function' ||
    typeof window.SLIDECART_SET_CART === 'function' ||
    typeof window.SLIDECART_OPEN === 'function'
  ) {
    return true;
  }
  return Boolean(
    document.querySelector('#slidecarthq') ||
      document.querySelector('[class*="slidecart"]')
  );
}

/**
 * Make Slide Cart's drawer reflect the freshly-mutated cart:
 *  1. Hand it the latest `/cart.js` payload via SLIDECART_SET_CART.
 *  2. Ask it to refetch + rerender via SLIDECART_UPDATE.
 *  3. Fire the generic `CartUpdated` / `cart:updated` / `cart:refresh` events
 *     that subscription apps (Skio, Recharge) and cart observers listen to.
 *  4. If Slide Cart's own DOM still shows fewer items than the server cart
 *     after its render tick, bounce it closed/open so it rebuilds from the
 *     current cart object — the reliable escape hatch when step 1/2 don't
 *     repaint on this build of Slide Cart.
 *  5. Re-open Slide Cart unconditionally when gifts have been added so the
 *     user always sees the full cart, not a half-rendered drawer.
 */
function cascadeBroadcastCartToApps() {
  if (typeof fetch !== 'function') {
    cascadeRefreshSlideCartIfPresent();
    return;
  }
  void fetch('/cart.js', { credentials: 'same-origin' })
    .then((r) => r.json())
    .then((cart) => {
      cascadeSlideCartSetCartIfPresent(cart);
      cascadeDispatchThirdPartyCartEvents(cart);
      cascadeRefreshSlideCartIfPresent();
      if (!cascadeHasSlideCart()) {
        return;
      }
      const expected =
        cart && typeof cart.item_count === 'number'
          ? cart.item_count
          : (cart && Array.isArray(cart.items) ? cart.items.length : 0);
      /* Give Slide Cart one paint to run its own render pipeline first. */
      setTimeout(() => {
        const wasOpen = cascadeSlideCartIsOpen();
        if (cascadeSlideCartIsMissingItems(expected)) {
          cascadeForceSlideCartRerenderCycle();
        } else if (wasOpen && typeof window.SLIDECART_OPEN === 'function') {
          /* Already correct — keep the drawer visible. */
          try {
            window.SLIDECART_OPEN();
          } catch (e) {
            /* no-op */
          }
        }
      }, 350);
    })
    .catch(() => {
      cascadeRefreshSlideCartIfPresent();
    });
}

/**
 * @param {any} response
 */
function cascadeAfterDrawerMorphNotifyThemeAndApps(response) {
  if (!cascadeCartResponseHasDrawerSections(response)) {
    return;
  }
  setTimeout(() => {
    cascadeDispatchModalCartAfterAdd(response);
    cascadeBroadcastCartToApps();
  }, 0);
}

/**
 * @returns {number[]}
 */
function cascadeGetBundleGiftIdsFromAllProductContainers() {
  const containers = document.querySelectorAll('[data-product-form-container]');
  for (let c = 0; c < containers.length; c += 1) {
    const ids = cascadeGetBundleFirstOrderGiftVariantIds(containers[c]);
    if (ids.length) {
      return ids;
    }
  }
  return [];
}

/**
 * JSON /cart/add.js: Skio, Slide Cart, and others POST { items: [{ id, selling_plan, ... }] }.
 * We must not treat our own follow-up gift line POSTs as "main" — those are only gift variant ids.
 */
function cascadeJsonCartAddBodyShouldTriggerGifts(jsonBody, giftIdList) {
  if (!jsonBody || !Array.isArray(jsonBody.items) || !jsonBody.items.length) {
    return false;
  }
  const numIds = jsonBody.items
    .map((it) => (it && it.id != null ? parseInt(String(it.id), 10) : 0))
    .filter((n) => n > 0);
  if (!numIds.length) {
    return false;
  }
  if (numIds.length && numIds.every((id) => giftIdList.indexOf(id) >= 0)) {
    return false;
  }
  return true;
}

/**
 * Extract the `id` (variant id) from an urlencoded body string, e.g.
 * `id=123&quantity=1&form_type=product`. Returns 0 when unparseable.
 * @param {string} body
 */
function cascadeUrlEncodedVariantId(body) {
  try {
    const params = new URLSearchParams(body);
    const id = params.get('id');
    return id ? parseInt(id, 10) : 0;
  } catch (e) {
    return 0;
  }
}

/**
 * Extract the `id` (variant id) from a FormData body. Returns 0 when absent.
 * @param {FormData} fd
 */
function cascadeFormDataVariantId(fd) {
  try {
    const id = fd.get('id');
    return id ? parseInt(String(id), 10) : 0;
  } catch (e) {
    return 0;
  }
}

/**
 * True when the request is a POST to cart add with a "main line" body (form),
 * not JSON { items: [...] }. Gift follow-ups (whose only `id` is one of our
 * gift variants) are treated as non-main so we don't recurse into ourselves.
 * @param {RequestInfo} input
 * @param {RequestInit} [init]
 * @param {number[]} [giftIdList]
 */
function cascadeIsFormMainLineCartAddPost(input, init, giftIdList) {
  const m = (init && init.method) || (input && /** @type {Request} */ (input).method) || 'GET';
  if (String(m).toUpperCase() !== 'POST') {
    return false;
  }
  const url = typeof input === 'string' ? input : (input && /** @type {Request} */ (input).url) || '';
  if (!/\/cart\/add(\.js)?(\?|$)/i.test(String(url))) {
    return false;
  }
  const gifts = giftIdList || [];
  if (init && init.body) {
    if (init.body instanceof FormData) {
      const vid = cascadeFormDataVariantId(init.body);
      if (vid && gifts.indexOf(vid) >= 0) return false;
      return true;
    }
    if (typeof init.body === 'string') {
      const t = init.body.trim();
      if (t.startsWith('{') || t.startsWith('[')) {
        return false;
      }
      if (!t.length) return false;
      const vid = cascadeUrlEncodedVariantId(t);
      if (vid && gifts.indexOf(vid) >= 0) return false;
      return true;
    }
  }
  if (typeof input !== 'string' && input instanceof Request) {
    const ct = (input.headers && input.headers.get && input.headers.get('content-type')) || '';
    if (ct.includes('application/json')) {
      return false;
    }
    if (
      /\/cart\/add(\.js)?(\?|$)/i.test(String(input.url || '')) &&
      String(input.method || 'GET').toUpperCase() === 'POST'
    ) {
      return true;
    }
  }
  return false;
}

/**
 * @param {RequestInfo} input
 * @param {RequestInit} [init]
 * @param {number[]} giftIdList
 */
function cascadeFetchShouldRunGiftAfterAdd(input, init, giftIdList) {
  if (cascadeIsFormMainLineCartAddPost(input, init, giftIdList)) {
    return true;
  }
  if (init && typeof init.body === 'string' && init.body.trim().startsWith('{')) {
    let parsed;
    try {
      parsed = JSON.parse(init.body);
    } catch (e) {
      return false;
    }
    return cascadeJsonCartAddBodyShouldTriggerGifts(/** @type {any} */ (parsed), giftIdList);
  }
  return false;
}

/**
 * @param {string} method
 * @param {string} url
 * @param {string | FormData | undefined | null} body
 * @param {number[]} giftIdList
 */
function cascadeXHRShouldRunGiftAfterAdd(method, url, body, giftIdList) {
  if (String(method || '').toUpperCase() !== 'POST' || !/\/cart\/add(\.js)?(\?|$)/i.test(String(url || ''))) {
    return false;
  }
  if (body instanceof FormData) {
    const vid = cascadeFormDataVariantId(body);
    if (vid && giftIdList.indexOf(vid) >= 0) return false;
    return true;
  }
  if (typeof body === 'string' && body.trim().startsWith('{')) {
    let parsed;
    try {
      parsed = JSON.parse(body);
    } catch (e) {
      return false;
    }
    return cascadeJsonCartAddBodyShouldTriggerGifts(
      /** @type {{ items?: { id: number }[] }} */ (parsed),
      giftIdList
    );
  }
  if (typeof body === 'string' && body.length > 0) {
    const vid = cascadeUrlEncodedVariantId(body);
    if (vid && giftIdList.indexOf(vid) >= 0) return false;
    return true;
  }
  return false;
}

/**
 * Inject `properties[_bundle_id]` + `properties[_bundle_role]=main` into a 3rd-party
 * `/cart/add.js` request body BEFORE it is sent, so the main line item gets tagged
 * the same way it does when our own Theme_Product.submit handler builds the request.
 * This is what lets Slide Cart / Skio / etc. participate in the bundle-cascade flow.
 *
 * Returns the bundleId we tagged with (or null if we didn't tag this request).
 *
 * @param {RequestInfo} input
 * @param {RequestInit} [init]
 * @param {number[]} giftIdList
 * @returns {string | null}
 */
function cascadeMaybeTagMainCartAddBody(input, init, giftIdList) {
  if (!init || !init.body) return null;
  if (init.method && String(init.method).toUpperCase() !== 'POST') return null;
  /* Only act on cart-add. POSTs (incl. /cart/add and /cart/add.js). */
  const url = typeof input === 'string' ? input : (input && /** @type {Request} */(input).url) || '';
  if (!/\/cart\/add(\.js)?(\?|$)/.test(String(url))) return null;
  if (!cascadeFetchShouldRunGiftAfterAdd(input, init, giftIdList)) return null;

  const body = init.body;
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    if (body.has('properties[_bundle_id]')) return /** @type {string} */ (body.get('properties[_bundle_id]'));
    const vid = parseInt(/** @type {string} */ (body.get('id')), 10);
    if (!vid || giftIdList.indexOf(vid) >= 0) return null;
    const bundleId = cascadeGenerateBundleId();
    body.append('properties[_bundle_id]', bundleId);
    body.append('properties[_bundle_role]', 'main');
    return bundleId;
  }
  if (typeof body === 'string' && body.length > 0) {
    if (body.indexOf('properties%5B_bundle_id%5D') >= 0 || body.indexOf('properties[_bundle_id]') >= 0) return null;
    const vid = cascadeUrlEncodedVariantId(body);
    if (!vid || giftIdList.indexOf(vid) >= 0) return null;
    const bundleId = cascadeGenerateBundleId();
    const sep = body.length > 0 ? '&' : '';
    /** @type {any} */ (init).body =
      body + sep + 'properties[_bundle_id]=' + encodeURIComponent(bundleId) + '&properties[_bundle_role]=main';
    return bundleId;
  }
  return null;
}

/**
 * @param {Response} res
 * @param {RequestInfo} input
 * @param {RequestInit} [init]
 * @param {string | null} [bundleId]
 */
function cascadeAfterMainCartAddAppendGifts(res, input, init, bundleId) {
  if (window.__cascadeBundleGiftFromTheme) {
    return Promise.resolve(res);
  }
  if (cascadeGiftAddRecentlyFired()) {
    return Promise.resolve(res);
  }
  if (!res || !res.ok) {
    return Promise.resolve(res);
  }
  const giftIdList = cascadeGetBundleGiftIdsFromAllProductContainers();
  if (!giftIdList.length) {
    return Promise.resolve(res);
  }
  if (document.querySelector('[x-data="Theme_GiftCardRecipient"]')) {
    return Promise.resolve(res);
  }
  if (!cascadeFetchShouldRunGiftAfterAdd(input, init, giftIdList)) {
    return Promise.resolve(res);
  }
  const _themeDrawer = window.theme && window.theme.settings && window.theme.settings.cart_type === 'drawer';
  /* Slide Cart acts as the drawer even when cart_type is "page". */
  const modalCart = _themeDrawer || cascadeHasSlideCart();
  window.__cascadeBundleGiftFromTheme = true;
  cascadeMarkGiftAddFired();
  return cascadeAddBundleGiftLineItems(giftIdList, { modalCart: Boolean(modalCart), bundleId: bundleId || undefined })
    .then((lastData) => cascadeUpdateDrawerAfterBundleGiftsComplete(lastData, Boolean(_themeDrawer)))
    .catch((err) => {
      if (err && console && console.error) {
        console.error('Bundle first-order gift add failed (fetch hook):', err);
      }
    })
    .then(() => {
      window.__cascadeBundleGiftFromTheme = false;
      return res;
    });
}

/* ============================================================================
 * Bundle cascade removal (orphan gift cleanup)
 *
 * Main and gift lines share `properties._bundle_id` and `._bundle_role` (`main`
 * or `gift`) — set in `cascadeAddBundleGiftLineItems` and the pre-send
 * /cart/add tagger.
 *
 * After any successful POST to `/cart/change`, `/cart/update`, or
 * `/cart/clear` (not `/cart/add`), we re-fetch the cart. If a `_bundle_id`
 * group has only `._bundle_role === "gift"` line(s) and no "anchor" line
 * (anything that is *not* role `gift` — typically the main, or a line with
 * missing role but same id), that means the main was removed: we remove
 * those gift line(s) in one `/cart/update.js`, then refresh the drawer/Slide
 * Cart like any quantity change. No pre-mutation cart snapshot.
 * ========================================================================= */

const CASCADE_BUNDLE_REMOVE_GUARD_MS = 6000;

/**
 * @returns {boolean} true while we are mid-cascade so additional change POSTs
 *   we generate ourselves don't trigger another cascade pass.
 */
function cascadeBundleRemoveInProgress() {
  return Boolean(window.__cascadeBundleRemoveInProgress);
}

/**
 * Test whether a request URL targets a cart-mutation endpoint we care about
 * for cascade removal: `/cart/change`, `/cart/change.js`, `/cart/update.js`,
 * `/cart/clear.js`, etc. (Note: `/cart/add` is excluded — that's pure adds.)
 *
 * @param {string} url
 * @returns {'change' | 'update' | 'clear' | null}
 */
function cascadeClassifyCartMutationUrl(url) {
  if (!url) return null;
  let u = String(url);
  try {
    if (u.indexOf('//') >= 0 && typeof URL !== 'undefined') {
      u = new URL(u, window.location && window.location.origin ? window.location.origin : 'https://shop.example').pathname;
    }
  } catch (e) {
    u = String(url);
  }
  if (/\/cart\/change(\.js)?(\?|$)/i.test(u)) return 'change';
  if (/\/cart\/update(\.js)?(\?|$)/i.test(u)) return 'update';
  if (/\/cart\/clear(\.js)?(\?|$)/i.test(u)) return 'clear';
  return null;
}

/**
 * @param {RequestInfo} input
 * @param {RequestInit} [init]
 * @returns {{ url: string, method: string }}
 */
function cascadeGetFetchUrlAndMethod(input, init) {
  let url = '';
  if (typeof input === 'string') {
    url = input;
  } else if (input && /** @type {Request} */ (input).url) {
    url = /** @type {Request} */ (input).url;
  }
  let method = 'GET';
  if (init && init.method) {
    method = String(init.method);
  } else if (input instanceof Request) {
    method = input.method || 'GET';
  }
  return { url, method: String(method).toUpperCase() };
}

/**
 * Read a line item property; supports object and {name, value}[] from edge APIs.
 *
 * @param {any} lineItem
 * @param {string} name
 * @returns {string | null}
 */
function cascadeBundleLineProp(lineItem, name) {
  const p = lineItem && lineItem.properties;
  if (p == null) return null;
  if (typeof p === 'object' && !Array.isArray(p)) {
    if (p[name] == null) return null;
    return String(p[name]);
  }
  if (Array.isArray(p)) {
    for (let i = 0; i < p.length; i += 1) {
      const row = p[i];
      if (row && String(row.name) === name) {
        return row.value == null ? null : String(row.value);
      }
    }
  }
  return null;
}

/**
 * @param {string} [url]
 * @param {string} [method]
 * @returns {boolean} whether to run orphan gift reconcile after a successful response
 */
function cascadeShouldRunReconcileAfterMutate(url, method) {
  if (String(method || '').toUpperCase() !== 'POST') return false;
  const u = String(url || '');
  if (/\/cart\/add(\.js)?(\?|$)/i.test(u)) return false;
  const kind = cascadeClassifyCartMutationUrl(u);
  return kind === 'change' || kind === 'update' || kind === 'clear';
}

/**
 * Fetch the current cart via the un-patched `fetch` so this does not re-enter
 * the cart-mutation hook chain.
 * @returns {Promise<any>}
 */
function cascadeFetchCart() {
  const of = (typeof window !== 'undefined' && window.__cascadeOrigFetch) || window.fetch;
  return of('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
    (r) => r.json()
  );
}

/**
 * @param {Record<string, number>} updates line keys to quantity
 * @returns {Promise<void>}
 */
function cascadePostCartUpdateRemoveBundleOrphans(updates) {
  const of = (typeof window !== 'undefined' && window.__cascadeOrigFetch) || window.fetch;
  const sections = CASCADE_CART_SECTIONS_DRAWER;
  return of('/cart/update.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      updates,
      sections,
      sections_url: window.location.pathname,
    }),
  })
    .then((r) => r.json())
    .then((data) => {
      if (!data) {
        cascadeBroadcastCartToApps();
        return;
      }
      return cascadeEnsureDrawerResponseWithSections(data).then((detail) => {
        if (cascadeCartResponseHasDrawerSections(detail)) {
          const evtType = 'cascade:modalcart:cartqtychange';
          const d = { response: detail };
          if (document.body) {
            document.body.dispatchEvent(new CustomEvent(evtType, { bubbles: true, detail: d }));
          }
          window.dispatchEvent(new CustomEvent(evtType, { bubbles: true, detail: d }));
          setTimeout(function () {
            cascadeBroadcastCartToApps();
          }, 0);
        } else {
          cascadeBroadcastCartToApps();
        }
      });
    })
    .catch((err) => {
      if (err && console && console.error) {
        console.error('Bundle orphan gift remove failed:', err);
      }
    })
    .finally(() => {
      cascadeLockBundleGiftCartLinesDebounced();
    });
}

/**
 * If the main/anchor is gone, remove all bundle `gift` lines for the same
 * `properties._bundle_id`. A line is a "gift" only when `._bundle_role ===
 * "gift"`. Any other line in that group (main, or missing/empty role) keeps
 * the bundle "alive" so we do not strip gifts while the main is still in cart.
 * @returns {Promise<void>}
 */
function cascadeReconcileOrphanBundleGifts() {
  if (cascadeBundleRemoveInProgress()) {
    return Promise.resolve();
  }
  return cascadeFetchCart()
    .then((cart) => {
      window.__cascadeLastCartJson = cart;
      if (!cart || !Array.isArray(cart.items)) {
        cascadeLockBundleGiftCartLinesDebounced();
        return;
      }
      const by = new Map();
      for (let i = 0; i < cart.items.length; i += 1) {
        const it = cart.items[i];
        if (!it || it.key == null) continue;
        const bid = cascadeBundleLineProp(it, '_bundle_id');
        if (bid == null) continue;
        if (!by.has(bid)) {
          by.set(bid, { anchors: [], gifts: [] });
        }
        const role = cascadeBundleLineProp(it, '_bundle_role');
        const gr = by.get(bid);
        if (role === 'gift') {
          gr.gifts.push(String(it.key));
        } else {
          /* main, or any non-gift — keeps the bundle from being "orphan" */
          gr.anchors.push(String(it.key));
        }
      }
      const toRemove = [];
      by.forEach((g) => {
        if (g.anchors.length === 0 && g.gifts.length) {
          toRemove.push.apply(toRemove, g.gifts);
        }
      });
      if (!toRemove.length) {
        cascadeLockBundleGiftCartLinesDebounced();
        return;
      }
      window.__cascadeBundleRemoveInProgress = true;
      setTimeout(() => {
        window.__cascadeBundleRemoveInProgress = false;
      }, CASCADE_BUNDLE_REMOVE_GUARD_MS);
      const updates = {};
      toRemove.forEach((k) => {
        updates[k] = 0;
      });
      return cascadePostCartUpdateRemoveBundleOrphans(updates);
    })
    .catch(() => {
      cascadeLockBundleGiftCartLinesDebounced();
    });
}

/**
 * @param {Response | { ok: boolean, status: number } | null} res
 * @param {{ url?: string, method?: string } | null} [ctx]
 * @returns {Promise<void>}
 */
function cascadeAfterCartChangeMaybeRemoveBundleSiblings(res, ctx) {
  if (!res || !res.ok) {
    return Promise.resolve();
  }
  if (cascadeBundleRemoveInProgress()) {
    return Promise.resolve();
  }
  if (!ctx || !cascadeShouldRunReconcileAfterMutate(ctx.url, ctx.method)) {
    return Promise.resolve();
  }
  return cascadeReconcileOrphanBundleGifts();
}

/* -------------------------------------------------------------------------- */
/* Bundle gift lines: lock UI in Slide Cart and other third-party drawers.    */
/* Theme `cart-items.liquid` handles native drawer HTML; this runs off       */
/* `/cart.js` and matches rows by `properties._bundle_role === "gift"`.         */
/* -------------------------------------------------------------------------- */

let __cascadeBundleGiftLockDebounceT = 0;
function cascadeBundleGiftEscape(s) {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(String(s));
  }
  return String(s).replace(/["\\#.:;=\[\]{}]/g, '\\$&');
}

function cascadeBundleGiftLockInstallStyles() {
  if (document.getElementById('cascade-bundle-gift-lock-css')) {
    return;
  }
  const st = document.createElement('style');
  st.id = 'cascade-bundle-gift-lock-css';
  st.textContent = [
    '/* Hide qty + remove; Slide Cart and theme use different class names. */',
    '.cascade-bundle-gift-line-locked a[href*="/cart/change"]',
    ', .cascade-bundle-gift-line-locked a[href*="/remove"]',
    ', .cascade-bundle-gift-line-locked a[href*="line="]:not([href*="/products/"])',
    ', .cascade-bundle-gift-line-locked button[aria-label]',
    ', .cascade-bundle-gift-line-locked [class*="remove"] a',
    ', .cascade-bundle-gift-line-locked [class*="close"] {',
    '  display: none !important; visibility: hidden !important; pointer-events: none !important;',
    '}',
    '.cascade-bundle-gift-line-locked [class*="quantity"] button,',
    '.cascade-bundle-gift-line-locked .flex button[aria-label],',
    '.cascade-bundle-gift-line-locked .quantity__button {',
    '  display: none !important; visibility: hidden !important;',
    '}',
    '.cascade-bundle-gift-line-locked input[type="number"] {',
    '  pointer-events: none !important; opacity: 0.45;',
    '}',
  ].join('');
  document.head.appendChild(st);
}

/**
 * @param {Element} root
 * @param {string} key
 * @param {any} item
 * @returns {Element | null}
 */
function cascadeFindDomLineForBundleGift(root, key, item) {
  if (!root || !key) {
    return null;
  }
  const k = cascadeBundleGiftEscape(String(key));
  const attempts = [
    `[data-cart-item-key="${k}"]`,
    `[data-line-key="${k}"]`,
    `[data-line-item-key="${k}"]`,
    `[data-line="${k}"]`,
    `[data-line-item-id="${k}"]`,
    `[data-key="${k}"]`,
    `[data-id="${k}"]`,
  ];
  for (let i = 0; i < attempts.length; i += 1) {
    let el = null;
    try {
      el = root.querySelector(attempts[i]);
    } catch (e) {
      el = null;
    }
    if (el) {
      return (
        el.closest(
          'li, article, tr, [class*="line-item"], [class*="LineItem"], [class*="CartItem"], [class*="cart-item"]'
        ) || el
      );
    }
  }
  if (item && item.variant_id) {
    const v = String(item.variant_id);
    let vels;
    try {
      vels = root.querySelectorAll(
        `[data-variant-id="${v}"], [data-variantid="${v}"], [line-variant-id="${v}"]`
      );
    } catch (e2) {
      vels = [];
    }
    if (vels && vels.length === 1) {
      const el = vels[0];
      return (
        el.closest(
          'li, article, tr, [class*="line-item"], [class*="LineItem"], [class*="CartItem"]'
        ) || el
      );
    }
  }
  if (item && item.title && String(item.title).length > 2) {
    const title = String(item.title).trim();
    const rows = root.querySelectorAll('li, [class*="line-item"], [class*="line-item-"], tr');
    for (let r = 0; r < rows.length; r += 1) {
      const row = rows[r];
      if (row && row.textContent && row.textContent.indexOf(title) >= 0) {
        return row;
      }
    }
  }
  return null;
}

/**
 * Move bundle gift rows to sit directly below their bundle main/anchor line.
 * Slide Cart and some drawers show newest lines first, so gifts added after the
 * main product can appear above it until we reorder the DOM.
 *
 * @param {any} cart
 */
function cascadeReorderBundleGiftLinesBelowMain(cart) {
  if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
    return;
  }
  /** @type {Map<string, { mains: any[], gifts: any[] }>} */
  const groups = new Map();
  for (let i = 0; i < cart.items.length; i += 1) {
    const it = cart.items[i];
    if (!it) continue;
    const bid = cascadeBundleLineProp(it, '_bundle_id');
    if (bid == null) continue;
    if (!groups.has(bid)) {
      groups.set(bid, { mains: [], gifts: [] });
    }
    const gr = groups.get(bid);
    const role = cascadeBundleLineProp(it, '_bundle_role');
    if (role === 'gift') {
      gr.gifts.push(it);
    } else {
      gr.mains.push(it);
    }
  }
  if (!groups.size) {
    return;
  }

  const roots = [
    document.getElementById('slidecarthq'),
    document.querySelector('[data-cart-items]'),
    document.getElementById('cart'),
  ];
  const uniqueRoots = [];
  roots.forEach((r) => {
    if (r && uniqueRoots.indexOf(r) < 0) {
      uniqueRoots.push(r);
    }
  });
  if (!uniqueRoots.length) {
    return;
  }

  groups.forEach((gr) => {
    if (!gr.mains.length || !gr.gifts.length) {
      return;
    }
    const mainItem = gr.mains[gr.mains.length - 1];
    const mainKey = mainItem && mainItem.key != null ? String(mainItem.key) : '';
    if (!mainKey) {
      return;
    }

    uniqueRoots.forEach((root) => {
      const mainEl = cascadeFindDomLineForBundleGift(root, mainKey, mainItem);
      if (!mainEl || !mainEl.parentNode) {
        return;
      }
      let insertAfter = mainEl;
      gr.gifts.forEach((giftItem) => {
        const giftKey = giftItem && giftItem.key != null ? String(giftItem.key) : '';
        if (!giftKey) {
          return;
        }
        const giftEl = cascadeFindDomLineForBundleGift(root, giftKey, giftItem);
        if (!giftEl || giftEl === insertAfter) {
          return;
        }
        const parent = insertAfter.parentNode;
        if (!parent) {
          return;
        }
        if (giftEl === insertAfter.nextElementSibling) {
          insertAfter = giftEl;
          return;
        }
        parent.insertBefore(giftEl, insertAfter.nextSibling);
        insertAfter = giftEl;
      });
    });
  });
}

/**
 * Fetches /cart.js, marks matching DOM rows; keeps last cart on window for
 * click-guard. Safe to call frequently (debounced at call sites).
 */
function cascadeLockBundleGiftCartLines() {
  if (typeof document === 'undefined' || !document.querySelector) {
    return;
  }
  cascadeBundleGiftLockInstallStyles();
  return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => {
      window.__cascadeLastCartJson = cart;
      if (!cart || !Array.isArray(cart.items)) {
        return;
      }
      const giftItems = cart.items.filter(
        (it) => it && it.properties && String(it.properties._bundle_role) === 'gift'
      );
      const roots = [
        document.getElementById('slidecarthq'),
        document.querySelector('[data-cart-items]'),
        document.getElementById('CartDrawer-Overlay'),
        document.getElementById('cart')
      ];
      const uniqueRoots = [];
      roots.forEach((r) => {
        if (r && uniqueRoots.indexOf(r) < 0) {
          uniqueRoots.push(r);
        }
      });
      if (!uniqueRoots.length) {
        uniqueRoots.push(document.body);
      }

      /* Clear previous (keys may have changed after edits). */
      document.querySelectorAll('.cascade-bundle-gift-line-locked').forEach((el) => {
        el.classList.remove('cascade-bundle-gift-line-locked');
        el.removeAttribute('data-cascade-bundle-gift');
      });

      if (!giftItems.length) {
        return;
      }

      uniqueRoots.forEach((root) => {
        if (!root) {
          return;
        }
        giftItems.forEach((it) => {
          const key = it.key != null ? String(it.key) : '';
          if (!key) {
            return;
          }
          const lineEl = cascadeFindDomLineForBundleGift(root, key, it);
          if (lineEl) {
            lineEl.classList.add('cascade-bundle-gift-line-locked');
            lineEl.setAttribute('data-cascade-bundle-gift', '1');
            if (!lineEl.getAttribute('data-cart-item-key')) {
              lineEl.setAttribute('data-cart-item-key', key);
            }
          }
        });
      });

      cascadeReorderBundleGiftLinesBelowMain(cart);
    })
    .catch(() => undefined);
}

function cascadeLockBundleGiftCartLinesDebounced() {
  clearTimeout(__cascadeBundleGiftLockDebounceT);
  __cascadeBundleGiftLockDebounceT = window.setTimeout(function () {
    void cascadeLockBundleGiftCartLines();
  }, 200);
}

function cascadeBundleGiftLockInstallClickGuard() {
  if (window.__cascadeBundleGiftClickGuard) {
    return;
  }
  window.__cascadeBundleGiftClickGuard = true;
  document.addEventListener(
    'click',
    function (e) {
      const t = e.target;
      if (!t || !t.closest) {
        return;
      }
      const row = t.closest('.cascade-bundle-gift-line-locked, [data-cascade-bundle-gift="1"]');
      if (!row) {
        return;
      }
      const prodA = t.closest('a[href*="/products/"]');
      if (prodA) {
        const href = prodA.getAttribute('href') || '';
        if (href.indexOf('cart') < 0 && href.indexOf('change') < 0) {
          return;
        }
      }
      if (
        t.closest('a[href*="/cart"]') ||
        t.closest('button') ||
        t.tagName === 'BUTTON' ||
        t.tagName === 'INPUT' ||
        t.tagName === 'SELECT' ||
        t.closest('input') ||
        t.closest('[class*="quantity"]') ||
        t.closest('[class*="remove"]') ||
        t.closest('[class*="close"]') ||
        t.closest('[class*="delete"]')
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );
}

(function cascadeInstallBundleGiftLockRuntime() {
  if (window.__cascadeBundleGiftLockRuntime) {
    return;
  }
  window.__cascadeBundleGiftLockRuntime = true;
  cascadeBundleGiftLockInstallStyles();
  cascadeBundleGiftLockInstallClickGuard();
  if (window.__cascadeLastCartJson == null) {
    void cascadeLockBundleGiftCartLines();
  }
  window.addEventListener('cascade:modalcart:cartqtychange', function () {
    cascadeLockBundleGiftCartLinesDebounced();
  });
  window.addEventListener('cascade:modalcart:afteradditem', function () {
    cascadeLockBundleGiftCartLinesDebounced();
  });
  /* Slide Cart re-renders from its own app — re-run lock when the drawer subtree changes. */
  (function installSlideCartGiftLockObserver() {
    const run = function () {
      cascadeLockBundleGiftCartLinesDebounced();
    };
    const hookRoot = function (sc) {
      if (!sc || sc.__cascadeBundleGiftSubMo) {
        return;
      }
      sc.__cascadeBundleGiftSubMo = true;
      const sub = new MutationObserver(run);
      sub.observe(sc, { childList: true, subtree: true });
    };
    const top = new MutationObserver(function (muts) {
      for (let i = 0; i < muts.length; i += 1) {
        const m = muts[i];
        if (!m.addedNodes) {
          continue;
        }
        m.addedNodes.forEach((n) => {
          if (n && /** @type {any} */ (n).id === 'slidecarthq') {
            hookRoot(/** @type {Element} */ (n));
            run();
          }
          if (n && /** @type {any} */ (n).querySelector) {
            const sc =
              /** @type {any} */ (n).id === 'slidecarthq'
                ? n
                : /** @type {any} */ (n).querySelector && /** @type {any} */ (n).querySelector('#slidecarthq');
            if (sc) {
              hookRoot(/** @type {Element} */ (sc));
              run();
            }
          }
        });
      }
    });
    if (document.documentElement) {
      top.observe(document.documentElement, { childList: true, subtree: true });
    }
    hookRoot(document.getElementById('slidecarthq'));
  })();
  if (typeof window.SLIDECART_UPDATE === 'function' && !window.__cascadeSlideCartUpdateWrapped) {
    const _su = window.SLIDECART_UPDATE;
    window.SLIDECART_UPDATE = function (cb) {
      return _su.call(
        this,
        typeof cb === 'function'
          ? function () {
              const r = cb.apply(this, arguments);
              cascadeLockBundleGiftCartLinesDebounced();
              return r;
            }
          : cb
      );
    };
    window.__cascadeSlideCartUpdateWrapped = true;
  }
})();

(function installCascadeBundleCartGiftHooks() {
  if (typeof window === 'undefined' || window.__cascadeBundleCartGiftHooks) {
    return;
  }
  window.__cascadeBundleCartGiftHooks = true;
  const origFetch = window.fetch.bind(window);
  window.__cascadeOrigFetch = origFetch;
  window.fetch = function (input, init) {
    /* PRE-send: tag the main /cart/add.js request body so the line item gets the bundle id.
       Skip when our own Theme_Product.submit already tagged it. */
    let preTaggedBundleId = null;
    if (!window.__cascadeBundleGiftFromTheme) {
      const giftIdListPre = cascadeGetBundleGiftIdsFromAllProductContainers();
      if (giftIdListPre.length) {
        preTaggedBundleId = cascadeMaybeTagMainCartAddBody(input, init, giftIdListPre);
      }
    }
    const ctx = cascadeGetFetchUrlAndMethod(input, init);
    return origFetch(input, init)
      .then((res) =>
        cascadeAfterCartChangeMaybeRemoveBundleSiblings(res, ctx).then(() => res)
      )
      .then((res) => cascadeAfterMainCartAddAppendGifts(res, input, init, preTaggedBundleId));
  };

  if (typeof XMLHttpRequest === 'undefined' || !XMLHttpRequest.prototype) {
    return;
  }
  const XO = XMLHttpRequest.prototype;
  const origOpen = XO.open;
  const origSend = XO.send;
  XO.open = function (method, url, ...rest) {
    this.__cascadeMethod = method;
    this.__cascadeUrl = typeof url === 'string' ? url : String(url);
    return origOpen.call(this, method, url, ...rest);
  };
  XO.send = function (body) {
    const xhr = this;
    let requestBody = body;
    const method = xhr.__cascadeMethod;
    const url = xhr.__cascadeUrl;

    /* Same as fetch hook: if this XHR is a main /cart/add, tag the body with `_bundle_id`
       before the network send so gift lines and cascade-remove share a stable group id. */
    let xhrPreTaggedBundleId = null;
    if (!window.__cascadeBundleGiftFromTheme) {
      const giftIdListForTag = cascadeGetBundleGiftIdsFromAllProductContainers();
      if (giftIdListForTag.length && requestBody != null) {
        const reqInit = { method, body: requestBody };
        xhrPreTaggedBundleId = cascadeMaybeTagMainCartAddBody(url, reqInit, giftIdListForTag);
        requestBody = reqInit.body;
      }
    }

    const sendXhr = function () {
      return origSend.call(xhr, requestBody);
    };
    const xhrCtx = { url, method };

    xhr.addEventListener(
      'load',
      function onLoad() {
        if (xhr.status >= 200 && xhr.status < 300) {
          void cascadeAfterCartChangeMaybeRemoveBundleSiblings(
            { ok: true, status: xhr.status },
            xhrCtx
          );
        }
        if (window.__cascadeBundleGiftFromTheme) {
          return;
        }
        if (cascadeGiftAddRecentlyFired()) {
          return;
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          return;
        }
        const giftIdList = cascadeGetBundleGiftIdsFromAllProductContainers();
        if (!giftIdList.length) {
          return;
        }
        if (document.querySelector('[x-data="Theme_GiftCardRecipient"]')) {
          return;
        }
        if (!cascadeXHRShouldRunGiftAfterAdd(method, url, requestBody, giftIdList)) {
          return;
        }
        const _themeDrawer2 = window.theme && window.theme.settings && window.theme.settings.cart_type === 'drawer';
        const modalCart = _themeDrawer2 || cascadeHasSlideCart();
        window.__cascadeBundleGiftFromTheme = true;
        cascadeMarkGiftAddFired();
        void cascadeAddBundleGiftLineItems(giftIdList, {
          modalCart: Boolean(modalCart),
          bundleId: xhrPreTaggedBundleId || undefined,
        })
          .then((lastData) => cascadeUpdateDrawerAfterBundleGiftsComplete(lastData, Boolean(_themeDrawer2)))
          .catch((err) => {
            if (err && console && console.error) {
              console.error('Bundle first-order gift add failed (XHR hook):', err);
            }
          })
          .then(() => {
            window.__cascadeBundleGiftFromTheme = false;
          });
      },
      { once: true }
    );

    return sendXhr();
  };
})();

window['Theme_Product'] = ({
  product,
  variant,
  featuredMediaId,
  enableImageZoom = false,
  enableThumbnailsOnDesktop = false
}) => {
  return {
    productForms: null,
    productRoot: null,
    product: product,
    currentVariant: variant,
    currentMediaId: featuredMediaId,
    loading: false,
    quantity: '1',
    optionHandles: [],
    storeAvailability: null,
    addedToCart: false,
    stickyAddToCartShown: false,
    variantChanged: false,
    updateStoreAvailability: null,
    videoInView: false,
    hasGiftCardRecipientForm: false,
    cartAddErrorMessage: '',
    lastClickedThumbnail: null,
    currentOption1: variant.option1,
    currentOption2: variant.option2,
    currentOption3: variant.option3,
    enableThumbnailsOnDesktop,
    get options() {
      let arr = [];
      if (this.currentOption1) {
        arr.push(this.currentOption1);
      }
      if (this.currentOption2) {
        arr.push(this.currentOption2);
      }
      if (this.currentOption3) {
        arr.push(this.currentOption3);
      }
      return arr;
    },
    get currentVariantId() {
      if (this.currentVariant) {
        return this.currentVariant.id;
      } else {
        return null;
      }
    },
    get currentVariantAvailabilityClosestLocation() {
      // this is on a lag to the actual current variant so that we can display an intermediary state while the fetch request is happening
      if (!Alpine.store('availability')) return null;

      const id = this.currentVariantId;
      const storeData = Alpine.store('availability').availability[id];

      if (storeData) {
        return storeData.closest_location;
      } else {
        return null;
      }
    },
    get currentVariantAvailable() {
      if (this.currentVariant) {
        return this.currentVariant.available;
      } else {
        return null;
      }
    },
    get currentVariantTitle() {
      if (this.currentVariant && this.currentVariant.title) {
        if (!this.currentVariant.title.includes('Default')) {
          return this.currentVariant.title;
        }
      }
      return '';
    },
    get current_price() {
      return this.currentVariant.price;
    },
    get isUsingSlideshowToDisplayMedia() {
      const splideEl = this.productRoot.querySelector('.splide');

      return splideIsNotDestroyed(splideEl);
    },
    formatMoney(price, moneyFormat = theme.defaultMoneyFormat) {
      return formatMoney(price, moneyFormat);
    },
    init() {
      // Set a product root for nested components
      // to use instead of $root (which refers to their root)
      this.productRoot = this.$root;

      if (this.$root.querySelector('[x-data="Theme_GiftCardRecipient"]')) {
        this.hasGiftCardRecipientForm = true;
      }

      this.updateStoreAvailability = debounce(
        this.__updateStoreAvailability.bind(this),
        150
      );

      this.productForm = this.$root.querySelector(
        `[data-product-form-container]`
      );

      const formEl = this.productForm && this.productForm.querySelector('.product-form');
      // Always intercept add-to-cart (drawer, page cart, bundle + gift line items) so
      // we never miss submit when the bundle block exists; single listener per form.
      if (formEl && !formEl.dataset.cascadeAddSubmitBound) {
        formEl.dataset.cascadeAddSubmitBound = 'true';
        formEl.addEventListener('submit', this.submitForm.bind(this), {
          capture: true,
        });
      }

      const addToCartBtnEl = this.$root.querySelector('.add-to-cart-btn');

      if (addToCartBtnEl.offsetHeight) {
        document.documentElement.style.setProperty(
          '--add-to-cart-button-height',
          `${addToCartBtnEl.offsetHeight}px`
        );
      }

      this.getOptionHandles();

      this.$root.addEventListener('cascade:product:slidechange', (e) => {
        this.currentMediaId = parseInt(e.detail.currentMediaId, 10);
      });

      this.$watch('currentMediaId', (value, oldValue) => {
        let shouldFocusWrapper = false;

        if (this.lastClickedThumbnail === value) {
          shouldFocusWrapper = true;
        }

        this.lastClickedThumbnail = null;

        const currentMediaObject = this.product.media.filter((media) => {
          return media.id === value;
        })[0];

        this.videoInView =
          currentMediaObject.media_type === 'video' ||
          currentMediaObject.media_type === 'external_video'
            ? true
            : false;

        if (this.isUsingSlideshowToDisplayMedia) {
          if (shouldFocusWrapper) {
            const newMediaWrapperEl = Array.from(
              this.$root.querySelectorAll(
                `[data-product-single-media-wrapper="${value}"]`
              )
            ).filter((mediaWrapperEl) =>
              Boolean(mediaWrapperEl.offsetParent)
            )[0];

            if (newMediaWrapperEl) {
              this.$focus.focus(newMediaWrapperEl);
            }
          }
        }

        if (this.enableThumbnailsOnDesktop) {
          this.$root.dispatchEvent(
            new CustomEvent('cascade:product:mediachange', {
              bubbles: true,
              detail: {
                media_id: this.currentMediaId,
                slideshow_id:
                this.$root.querySelector('.splide--product')?.id,
              },
            })
          );
        }

        // There can be more than one media (e.g. for different breakpoints)
        // so we check the offsetHeight to see if the wrapper could currently
        // be visible

        // https://davidwalsh.name/offsetheight-visibility

        this.$root
          .querySelectorAll(`[data-product-single-media-wrapper="${oldValue}"]`)
          .forEach((mediaWrapperEl) => {
            if (Boolean(mediaWrapperEl.offsetHeight)) {
              mediaWrapperEl.dispatchEvent(new CustomEvent('mediaHidden'));
            }
          });

        this.$root
          .querySelectorAll(`[data-product-single-media-wrapper="${value}"]`)
          .forEach((mediaWrapperEl) => {
            if (Boolean(mediaWrapperEl.offsetHeight)) {
              mediaWrapperEl.dispatchEvent(new CustomEvent('mediaVisible'));
            }
          });
      });

      this.updateStoreAvailability(this.currentVariant);
    },
    __updateStoreAvailability(variant) {
      if (!this.$refs.storeAvailabilityContainer) return;

      this.storeAvailability =
        this.storeAvailability ||
        new StoreAvailability(this.$refs.storeAvailabilityContainer);

      if (this.storeAvailability && variant) {
        this.storeAvailability.fetchContent(variant);
      }
    },
    optionChange() {
      this.getOptionHandles();

      const matchedVariant = ShopifyProduct.getVariantFromOptionArray(
        this.product,
        this.options
      );

      this.currentVariant = matchedVariant;

      if (this.currentVariant) {
        variantLiveRegion(this.currentVariant);
        this.updateStoreAvailability(this.currentVariant);

        if (this.currentVariant.featured_media) {
          this.currentMediaId = this.currentVariant.featured_media.id;
        }

        const url = ShopifyProductForm.getUrlWithVariant(
          window.location.href,
          this.currentVariant.id
        );

        this.$root.dispatchEvent(
          new CustomEvent('product-url-update', {
            bubbles: true,
            detail: { url: url },
          })
        );

        const inFormNameEl =
          this.$refs.singleVariantSelector || this.$refs.productFormNameField;

        inFormNameEl.dispatchEvent(new Event('change', { bubbles: true }));

        this.$root.dispatchEvent(
          new CustomEvent('cascade:product:variantchange', {
            bubbles: true,
            detail: { variant: this.currentVariant },
          })
        );

        this.variantChanged = true;
      }
    },
    getOptionHandles() {
      this.optionHandles = [];

      const selectorEl = this.productForm.querySelectorAll(
        '[data-single-option-selector]'
      );

      if (selectorEl.nodeName === 'SELECT') {
        this.optionHandles.push(
          selectorEl.options[selectorEl.selectedIndex].dataset.handle
        );
      } else {
        if (selectorEl.checked) {
          this.optionHandles.push(selectorEl.dataset.handle);
        }
      }
    },
    submitForm(evt) {
      evt.preventDefault();
      this.loading = true;
      this.addedToCart = false;

      liveRegion(window.theme.strings.loading);

      const formData = new FormData(evt.target);
      const formId = evt.target.getAttribute('id');

      const modalCart = theme.settings.cart_type === 'drawer';
      /* Slide Cart acts as the drawer even when cart_type = "page". */
      const useDrawer = modalCart || cascadeHasSlideCart();

      const handleCartAddResponse = (data) => {
        if (data.status) {
          this.loading = false;

          const errors = data.errors || data.description;
          const message = data.description || data.message;

          if (!this.hasGiftCardRecipientForm) {
            this.cartAddErrorMessage =
              message || window.theme.strings.cartError;
          }

          document.body.dispatchEvent(
            new CustomEvent('cascade:cart:adderror', {
              detail: {
                source: 'product-form',
                sourceId: formId,
                variantId: formData.get('id'),
                errors,
                message,
              },
            })
          );
          return;
        }

        this.loading = false;
        this.cartAddErrorMessage = null;
        this.addedToCart = true;

        if (useDrawer) {
          setTimeout(() => {
            if (modalCart) cascadeDispatchModalCartAfterAdd(data);
            cascadeBroadcastCartToApps();
          }, 0);
        } else if (theme.routes && theme.routes.cart_url) {
          window.location.assign(theme.routes.cart_url);
          return;
        }

        if (!document.querySelector('[data-show-on-add="true"]')) {
          if (this.$refs.added) this.$nextTick(() => this.$refs.added.focus());
        }
      };

      const pfc = evt.target.closest('[data-product-form-container]');
      const giftIdList = !this.hasGiftCardRecipientForm
        ? cascadeGetBundleFirstOrderGiftVariantIds(pfc || this.$root)
        : [];

      if (giftIdList.length > 0) {
        /* First request = native FormData (Skio, subscription). Gift lines = /cart/add.js each.
             fetch() hook in this file also appends gifts when a third party (e.g. Slide Cart) POSTs
             FormData to cart add without using this submit handler.

             We tag the main + every gift line with a shared `_bundle_id` so the cascade-remove
             hook can detect them as siblings if the customer (or any third-party drawer) deletes
             one of them later. */
        window.__cascadeBundleGiftFromTheme = true;
        cascadeMarkGiftAddFired();
        const bundleId = cascadeGenerateBundleId();
        const fdMain = new FormData(evt.target);
        fdMain.append('properties[_bundle_id]', bundleId);
        fdMain.append('properties[_bundle_role]', 'main');

        const run = fetch(`${theme.routes.cart_add_url}`, {
          method: 'POST',
          body: fdMain,
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
          .then((res) => res.text().then((text) => cascadeParseCartAddResponse(res, text)))
          .then((data) => {
            if (data && data.status) {
              return Promise.reject(data);
            }
            /* useDrawer = true when Slide Cart is present (even if cart_type = "page") */
            return cascadeAddBundleGiftLineItems(giftIdList, { modalCart: useDrawer, bundleId });
          })
          .then((giftsLast) => {
            return cascadeEnsureDrawerResponseWithSections(
              giftsLast || /** @type {any} */ ({})
            ).then((d) => handleCartAddResponse(d || /** @type {any} */ ({})));
          })
          .catch((err) => {
            this.loading = false;
            if (
              err &&
              typeof err === 'object' &&
              (err.status != null || err.message || err.description || err.errors)
            ) {
              handleCartAddResponse(err);
            } else {
              console.log(err);
            }
          })
          .finally(() => {
            window.__cascadeBundleGiftFromTheme = false;
          });
        void run;
        return;
      }

      const config = fetchConfigDefaults('javascript');

      if (useDrawer) {
        formData.append('sections', CASCADE_CART_SECTIONS_DRAWER);
        formData.append('sections_url', window.location.pathname);
      }

      config.body = formData;

      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((res) => res.json())
        .then((data) => handleCartAddResponse(data))
        .catch((error) => {
          console.log(error);
        });
    },
    openZoom(mediaId) {
      const zoomModalId = `image-zoom-${this.productRoot.id}`;

      if (!this.$store.modals.modals[zoomModalId]) {
        this.$store.modals.register(zoomModalId, 'modal');
      }

      this.$watch('$store.modals.modal.contents', (val) => {
        if (val === zoomModalId) {
          this.$nextTick(() => {
            const zoomModalEl = document.getElementById(zoomModalId);

            waitForContent(zoomModalEl).then(() => {
              const mediaEl = zoomModalEl.querySelector(
                `[data-media-id="${mediaId}"]`
              );

              if (mediaEl) {
                mediaEl.scrollIntoView();
              }
            });
          });
        }
      });

      this.$store.modals.open(zoomModalId);
    },
  };
};
